import flatten from 'lodash/flatten.js';
import uniq from 'lodash/uniq.js';
import values from 'lodash/values.js';

export const ANNOUNCEMENT_DEFAULT_VALIDATION_MESSAGE =
    'This announcement could not be published. Please check the attachments and try again.';

/**
 * The most messages worth putting in a single snackbar. Attaching several bad files would
 * otherwise produce a wall of text the user has to read before the toast times out.
 */
const MAX_MESSAGES = 3;

function usableMessages(errors) {
    if (!errors || typeof errors !== 'object') {
        return [];
    }

    return uniq(flatten(values(errors)).filter(message => typeof message === 'string' && message.trim().length > 0));
}

/**
 * Build the message this form should show for a failed publish.
 *
 * Only 422s get one. `handleResponseError` deliberately suppresses the global snackbar for
 * validation errors (Helpers.js) because the AutoSave system normally renders them field by
 * field — but the announcement form has no field-level error UI, so without this a server
 * rejection looks exactly like a silent no-op. Every other status already raises the generic
 * snackbar, and returning null here keeps this form from stacking a second one on top.
 *
 * @param {unknown} error
 * @returns {string|null}
 */
export function announcementValidationMessage(error) {
    const response = error?.response;

    if (response?.status !== 422) {
        return null;
    }

    const data = response?.data;

    if (!data || typeof data !== 'object') {
        return ANNOUNCEMENT_DEFAULT_VALIDATION_MESSAGE;
    }

    const messages = usableMessages(data.errors);

    if (messages.length > 0) {
        return messages.slice(0, MAX_MESSAGES).join(' ');
    }

    return typeof data.message === 'string' && data.message.trim().length > 0
        ? data.message.trim()
        : ANNOUNCEMENT_DEFAULT_VALIDATION_MESSAGE;
}
